/* -------------------------- Design imports start -------------------------- */
import React, { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Filesystem } from './Filesystem'
import S3FileUpload from './S3FileUpload'
import AccessFilesModal from '../../../components/widgets/AccessFilesModal'
import TextfieldModal from '../../../components/widgets/NewFolderDialog'

/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */

import LogTool from '../../../logger/logTools'
import { downloadBlob } from '../../../utils/functions'
import StlViewerModal from '../../StlViewer/StlViewerModal'
import { type Folder, type FileMetadata } from '../utils/types'
import { useFilesInFolder } from '../hooks/useFilesystemQueries'
import { useCreateFolder, useDeleteFile, useDeleteFolder } from '../hooks/useFilesystemMutations'
import { downloadFile as downloadFileApi } from '../api/fileApi'
import { useQueryClient } from '@tanstack/react-query'
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
/**
 * Logic needed to make whole Filesystem work
 * Handles:
 *  - uploads
 *  - Navigation paths and history
 *  - deleting/downloading files
 *  - creating new folders
 */
export const FilesystemLogic = (): ReactElement => {
  /* -------------------------- Non state data start -------------------------- */
  const { t } = useTranslation(['files'])
  const log = new LogTool({ context: 'FileSystemLogic', enable: true })
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- React Query Hooks --------------------------- */
  const queryClient = useQueryClient()
  const [path, setPath] = useState<string>('')
  const { data: filesystemData, isLoading } = useFilesInFolder(path)
  const createFolderMutation = useCreateFolder()
  const deleteFileMutation = useDeleteFile()
  const deleteFolderMutation = useDeleteFolder()
  /* ---------------------------- React Query Hooks End ----------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [openNewFolderDialog, setNewFolderDialog] = useState<boolean>(false)
  const [openDeleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [openDownloadDialog, setDownloadDialog] = useState<boolean>(false)
  const [openStlViewer, setOpenStlViewer] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  // files with RenderTree file structure
  const files = filesystemData?.files || []
  const folders = filesystemData?.directories || []
  // depth in file structure to determine current position
  const [depth, setDepth] = useState<number>(1)
  // history saved for navigating backward and forward
  const [history, setHistory] = useState<string>('')
  // selected files to delete/download
  const [tickedFiles, setTickedFiles] = useState<string[]>([])
  const [tickedFolders, setTickedFolders] = useState<string[]>([])
  const [newFolderName, setNewFolderName] = useState<string>('')
  /* ----------------------------- Data states end ---------------------------- */
  log.debug('path', path)

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    // create new folder as admin or user
    if (typeof newFolderName !== 'undefined' && newFolderName.length !== 0) {
      void (async () => {
        await createFolder(newFolderName)
      })()
      setNewFolderName('')
    }
  }, [newFolderName])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */

  /**
   * Refresh the current folder content
   */
  async function refreshFilesInFolder(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['filesystem', 'folder', path] })
  }

  /**
   * Create a new folder by creating a empty file with a
   * filepath that ends with "/"
   * @param folderName
   */
  async function createFolder(folderName: string): Promise<void> {
    try {
      await createFolderMutation.mutateAsync({ path, folderName })
      toast.success(t('common:feedback.success.folderCreated'))
      await refreshFilesInFolder()
    } catch (error) {
      log.error('Error creating folder', error)
      toast.error(t('common:feedback.error.folderCreationError'))
    }
  }

  /**
   * Download one or more multiple files in parallel
   * @param files
   */
  async function downloadFiles(files: string[]): Promise<void> {
    const downloadPromises = files.map(async file => {
      await downloadFile(file)
    })
    await Promise.all(downloadPromises)
      .then(() => {
        toast.success(t('common:feedback.success.filesDownloaded'))
        return
      })
      .catch(() => {
        toast.error(t('common:feedback.error.filesDownloadError'))
      })
  }

  /**
   * Download file using a blob thats returned from API
   * @param file
   * @returns status of download
   */
  async function downloadFile(file: string): Promise<void> {
    try {
      const filePath = path + '/' + file
      const blob = await downloadFileApi(filePath)
      downloadBlob(blob, file)
    } catch (error) {
      log.error('Error downloading file', error)
      toast.error(t('common:feedback.error.errorDownloadingFile'))
    }
  }

  /**
   * Delete multiple files/folders in parallel
   * @param files
   */
  async function deleteFiles(files: string[]): Promise<void> {
    log.info('Begin deleting files/folders.')
    if (files.length === 0) return
    
    try {
      const promises = files.map((file: any) =>
        tickedFolders.includes(file) ? deleteFolder(file) : deleteFile(file)
      )
      await Promise.all(promises)
      await refreshFilesInFolder()
      toast.success(t('common:feedback.success.filesDeleted'))
    } catch (error) {
      log.error('Error deleting files', error)
      toast.error(t('common:feedback.error.filesDeleteError'))
    }
  }

  /**
   * Delete file
   * @param file
   */
  const deleteFile = async (file: string): Promise<void> => {
    const filePath = path + '/' + file
    await deleteFileMutation.mutateAsync(filePath)
  }

  /**
   * Delete folder
   * @param folder
   */
  const deleteFolder = async (folder: string): Promise<void> => {
    const folderPath = path + '/' + folder
    await deleteFolderMutation.mutateAsync(folderPath)
  }

  /* ------------------------- Utility functions start ------------------------ */

  /* ------------------------ Callback functions start ------------------------ */

  const onFileClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, file: string): void => {
    if (!tickedFiles.includes(file)) {
      setTickedFiles([...tickedFiles, file])
    } else {
      setTickedFiles(tickedFiles.filter(tickedFile => tickedFile !== file))
    }
  }

  const onFolderClick = (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    folder: string
  ): void => {
    setPath(path + '/' + folder)
    // update history if folder is not in history
    setHistory(history.includes(folder) ? history : '')
    setDepth(depth + 1)
    setTickedFiles([])
  }

  const handleBreadcrumbClick = (currentPath: string, folder: string): void => {
    setDepth(folder.split('/').length)
    // cut off path after folder
    folder !== '' && currentPath.includes(folder)
      ? setPath(currentPath.substring(0, currentPath.indexOf(folder) + folder.length))
      : setPath('')
    setTickedFiles([])
  }

  const onDownloadFileClick = async (): Promise<void> => {
    await downloadFiles(tickedFiles)
  }

  const onDeleteFileClick = (): void => {
    setDeleteDialog(true)
  }

  const onHistoryBackClick = (): void => {
    setHistory(history.includes(path) ? history : path)
    setPath(path.substring(0, path.lastIndexOf('/')))
    setDepth(depth - 1)
    setTickedFiles([])
  }

  const onHistoryForwardClick = (): void => {
    setPath(
      history
        .split('/')
        .slice(0, depth + 1)
        .join('/')
    )
    setDepth(depth + 1)
    setTickedFiles([])
  }

  const onShowStlClick = (): void => {
    log.info('onShowStlClick')
    setOpenStlViewer(true)
  }

  const onCreateFolderClick = (): void => {
    setNewFolderDialog(true)
  }

  const updateTickedStatus = (file: string, type: 'FILE' | 'FOLDER' = 'FILE'): void => {
    //if file is ticked, untick it, else tick it
    log.debug(type)
    if (getTickedStatus(file) !== -1) {
      setTickedFiles(tickedFiles.filter(f => f !== file))
      if (type === 'FOLDER') {
        setTickedFolders(tickedFolders.filter(f => f !== file))
      }
    } else {
      setTickedFiles([...tickedFiles, file])
      if (type === 'FOLDER') {
        setTickedFolders([...tickedFolders, file])
      }
    }
  }

  const getTickedStatus = (file: string): number => {
    //if file is ticked return index of file in tickedFiles, else return -1
    return tickedFiles.indexOf(file)
  }

  const onTickAllFilesClick = (): void => {
    setTickedFiles(files.map(file => file.Name))
  }

  const onUntickAllFilesClick = (): void => {
    setTickedFiles([])
  }

  const onRenameFileClick = (): void => {
    log.info('onRenameFileClick')
  }

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  if (!folders || !files) {
    return <div>Loading...</div>
  }

  log.debug('tickedFiles', tickedFiles)
  log.debug('files', files)
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <div>
      <S3FileUpload uploadPath={path} fetchFiles={refreshFilesInFolder} />
      <Filesystem
        files={files}
        folders={folders}
        tickedFiles={tickedFiles}
        onFileClick={onFileClick}
        onFolderClick={onFolderClick}
        history={history}
        currentPath={path}
        currentDepth={depth}
        handleBreadcrumbClick={handleBreadcrumbClick}
        onHistoryForwardClick={onHistoryForwardClick}
        onHistoryBackClick={onHistoryBackClick}
        updateTickedStatus={updateTickedStatus}
        getTickedStatus={getTickedStatus}
        onTickAllFilesClick={onTickAllFilesClick}
        onUntickAllFilesClick={onUntickAllFilesClick}
        onShowStlClick={onShowStlClick}
        onCreateFolderClick={onCreateFolderClick}
        onRenameFileClick={onRenameFileClick}
        onDownloadFileClick={onDownloadFileClick}
        onDeleteFileClick={onDeleteFileClick}
        allowCreateFolder={true}
        allowRename={tickedFiles.length === 1}
        allowDownload={tickedFiles.length > 0}
        allowDelete={tickedFiles.length > 0}
        allowShowStl={tickedFiles.length === 1 && tickedFiles[0].endsWith('.stl')}
      />
      <TextfieldModal
        title={t('interaction.button.createFolder')}
        textfieldLabel={t('content.label.folderName') }
        fallbackValue={t('content.label.createFolderFallbackValue')}
        open={openNewFolderDialog}
        setOpen={setNewFolderDialog}
        fetchValueBy={setNewFolderName}
      />
      <StlViewerModal
        open={openStlViewer}
        setOpen={setOpenStlViewer}
        url={files.find(file => file.Name === tickedFiles[0])?.Url as string}
      />
      <AccessFilesModal
        open={openDownloadDialog}
        setOpen={setDownloadDialog}
        title={t('interaction.button.downloadFiles')}
        description={t('feedback.info.areYouSureDownload') }
        files={tickedFiles}
        onConfirm={() => downloadFiles(tickedFiles)}
      />
      <AccessFilesModal
        open={openDeleteDialog}
        setOpen={setDeleteDialog}
        title={t('interaction.button.deleteFiles')}
        description={t('feedback.info.areYouSureDeleteFiles') }
        files={tickedFiles}
        onConfirm={() => {
          void (async () => {
            await deleteFiles(tickedFiles)
          })()
          setTickedFiles([])
        }}
      />
    </div>
  )
}
